import React, { MouseEvent, useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import RefreshIcon from '@mui/icons-material/Refresh';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, Stack, Typography } from '@mui/material';

import useAccommodationPropertyDetailsContext from '~/components/Accommodation/Pages/PropertyDetails/context/useAccommodationPropertyDetailsContext';
import mappers from '~/components/DebugModal/mappers';
import { TraverseObject } from '~/components/DebugModal/utils';

import { useGetPropertyDebug, useRefreshPropertyDebug } from '~/hooks/apis/accommodation/usePropertyDebug';

import { getTimeDifference } from '~/services/TimeService';

export default function AccommodationPropertyDebugModal() {
  const [open, setOpen] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const { propertyDetails } = useAccommodationPropertyDetailsContext();

  const {
    data: debugData,
    isLoading,
    refetch,
    dataUpdatedAt,
  } = useGetPropertyDebug({
    propertyId: propertyDetails.id,
    enabled: open,
  });

  const refreshMutation = useRefreshPropertyDebug();

  const openDebugModal = useCallback(() => {
    setOpen(true);
  }, []);

  const closeDebugModal = useCallback(() => {
    setOpen(false);
  }, []);

  const importantMap = useMemo(() => {
    return (mappers['generic'] ?? (() => null))(debugData);
  }, [debugData]);

  const handleRegenerate = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation();
      try {
        await refreshMutation.mutateAsync({ propertyId: propertyDetails.id });
        enqueueSnackbar('Property debug data refreshed successfully', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(`Refresh failed: ${error.message}`, { variant: 'error' });
      }
    },
    [refreshMutation, propertyDetails.id, enqueueSnackbar],
  );

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const lastRetrievedAtText = useCallback(() => {
    if (!dataUpdatedAt) {
      return '';
    }
    const lastRetrievedAt = new Date(dataUpdatedAt);
    const diff = getTimeDifference(lastRetrievedAt, new Date(), true, true);
    if (diff) {
      return `${diff} ago`;
    }
    return 'Just now';
  }, [dataUpdatedAt]);

  return (
    <>
      <Button onClick={openDebugModal} title="Open debug modal" variant="contained" size="small">
        Debug Modal
      </Button>

      <Dialog open={open} maxWidth="lg" onClose={closeDebugModal}>
        <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
          <Typography variant="h6">Property Debug</Typography>
          {dataUpdatedAt && (
            <Stack direction="row" gap={2} alignItems="center" sx={{ flex: 1, justifyContent: 'flex-end' }}>
              <Typography>Last Retrieved: {lastRetrievedAtText()}</Typography>
              <Button variant="outlined" onClick={handleRefresh}>
                <RefreshIcon />
              </Button>
            </Stack>
          )}
        </Stack>

        <DialogContent sx={{ minWidth: '50vw' }}>
          {(isLoading || refreshMutation.isPending) && <CircularProgress />}
          {importantMap !== null && <TraverseObject obj={importantMap} />}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Stack direction="row" gap={2}>
            <Button
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(JSON.stringify(debugData, null, 2));
                enqueueSnackbar('JSON copied to clipboard', { variant: 'success' });
              }}
            >
              Copy JSON to Clipboard
            </Button>
            <Button
              variant="outlined"
              onClick={handleRegenerate}
              disabled={refreshMutation.isPending}
              startIcon={refreshMutation.isPending ? <CircularProgress size={16} /> : undefined}
            >
              {refreshMutation.isPending ? 'Regenerating...' : 'Regenerate state and cache'}
            </Button>
          </Stack>
          <Stack direction="row" gap={2}>
            <Button
              variant="outlined"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
            >
              Close
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
}
